// src/hooks/useApiData.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext.jsx';

// Fase 5 (B3): proteção contra race condition em troca rápida de cliente.
//
// Cenário problemático sem proteção:
//   t=0   request A disparada (client=A)        -> in-flight
//   t=20  usuário troca para B; load() dispara
//   t=21  request B disparada (client=B)        -> in-flight
//   t=25  resposta B chega -> setData(B)        OK
//   t=80  resposta A chega ATRASADA            -> setData(A)  ← stale!
//
// Solução adotada: request versioning + mounted ref.
//   - Cada chamada de `load` recebe um número de geração (`gen`).
//   - `genRef.current` rastreia a geração mais recente.
//   - Ao retornar de `fetchFn`, comparamos a geração local com a atual.
//     Se diferente, descartamos o resultado (sem setState).
//   - `mountedRef` impede setState após unmount.
//
// Não cancelamos a request HTTP (precisaria AbortController + refactor de
// `db/api.js` para aceitar `signal`). O resultado obsoleto é apenas
// descartado no client. Banda extra é o trade-off; segurança de UI é o ganho.

export function useApiData(fetchFn, deps = [], options = {}) {
  const { initialData = null, skip = false } = options;
  const app = useApp();
  const activeClientId = app?.activeClient?.id ?? null;

  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState(null);

  const genRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (skip) return;
    const myGen = ++genRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchFn();
      if (!mountedRef.current) return;
      if (myGen !== genRef.current) return; // resultado obsoleto, descarta
      setData(result);
    } catch (err) {
      if (!mountedRef.current) return;
      if (myGen !== genRef.current) return; // erro obsoleto, descarta
      setError(err?.message || 'Erro desconhecido');
    } finally {
      if (mountedRef.current && myGen === genRef.current) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, activeClientId, skip]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
}
